import React, { useEffect, useState, useRef } from 'react';
import { axiosInstance } from "../../utils/axios.js";
import { useParams, NavLink } from 'react-router-dom';
import { Calendar, Clock, User, ChevronLeft, ChevronRight, Sunrise, Sun, Moon, CheckCircle, AlertCircle } from 'lucide-react';
import { toJstDateInputValue } from "../../utils/dateTimeJst";

const ReserveTakeContainer = (props) => {
  const { type, id } = useParams();
  const [title, setTitle] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [appointmentType, setAppointmentType] = useState('initial');
  const [shiftsData, setShiftsData] = useState([]);
  const [error, setError] = useState(null);
  const [doctorId, setDoctorId] = useState("any");
  const [doctors, setDoctors] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [headerDates, setHeaderDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // ここで、スクロール対象のコンテナ全体に ref を張る
  const scheduleContainerRef = useRef(null);

  // 各時間帯の先頭行に ref を張る
  const morningRef = useRef(null);   // 08:00 の行
  const afternoonRef = useRef(null); // 12:00 の行
  const eveningRef = useRef(null);   // 18:00 の行

  // 時間帯（10:00～23:00）の区間を生成
  const generateTimeIntervals = () => {
    const intervals = [];
    for (let h = 8; h < 24; h++) {
      intervals.push({
        label: `${String(h).padStart(2, '0')}:00～`,
        start: `${String(h).padStart(2, '0')}:00`,
        end: `${String(h + 1).padStart(2, '0')}:00`
      });
    }
    return intervals;
  };

  // 5分刻みのシフトを連続グループにまとめる
  const groupConsecutiveShifts = (shifts) => {
    if (!shifts || shifts.length === 0) return [];
    const sorted = [...shifts].sort(
      (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
    let groups = [];
    let currentGroup = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const prevTime = new Date(sorted[i - 1].start_time);
      const currTime = new Date(sorted[i].start_time);
      const diffInMinutes = (currTime.getTime() - prevTime.getTime()) / (1000 * 60);
      if (diffInMinutes === 5) {
        currentGroup.push(sorted[i]);
      } else {
        groups.push(currentGroup);
        currentGroup = [sorted[i]];
      }
    }
    if (currentGroup.length) groups.push(currentGroup);
    return groups;
  };

  // 指定された時間帯内のシフトをフィルタリング
  const filterShiftsByInterval = (shifts, startTime, endTime) => {
    const startHour = parseInt(startTime.split(':')[0]);
    const endHour = parseInt(endTime.split(':')[0]);
    return shifts.filter(shift => {
      const shiftDate = new Date(shift.start_time);
      const hour = shiftDate.getHours();
      return hour >= startHour && hour < endHour;
    });
  };

  // HH:mm形式で時刻を返す
  const formatTime = (dateTime) => {
    const date = new Date(dateTime);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  // 5分を加算して終了時刻を算出
  const addMinutesToDateTime = (dateTime, minutesToAdd) => {
    const date = new Date(dateTime);
    date.setMinutes(date.getMinutes() + minutesToAdd);
    return date;
  };

  // 初回：医師一覧を取得
  useEffect(() => {
    setIsLoading(true);
    axiosInstance.get(`/api/medichat/reservations/doctors_list/${id}`, {
      headers: {
        'uid': props.token,
        'Access-Control-Allow-Origin': '*'
      }
    })
      .then(response => {
        setDoctors(response.data);
        setIsLoading(false);
      })
      .catch(err => {
        setError('医師一覧の取得に失敗しました');
        setIsLoading(false);
      });
  }, [id, props.token]);

  // ヘッダーの日付生成（週の開始日＝当日から weekOffset*7 日後）
  useEffect(() => {
    const dates = [];
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() + weekOffset * 7);
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      dates.push(d);
    }
    setHeaderDates(dates);
    setSelectedDate(dates[0]);
  }, [weekOffset]);

  // 選択中の日付・医師に応じて、その日のシフトをAPIから取得
  useEffect(() => {
    if (!selectedDate) return;
    setIsLoading(true);

    // URLパラメータ type に応じてタイトルをセット
    if (type === "hypertension") {
      setTitle('高血圧治療');
    } else if (type === "gout") {
      setTitle('痛風（高尿酸血症）');
    } else if (type === "diabetes") {
      setTitle('糖尿病');
    } else {
      setTitle('高脂血症');
    }

    const selectedDateStr = formatLocalDate(selectedDate);
    axiosInstance.get(`/api/medichat/reservations/${id}/shift?date=${selectedDateStr}&doctor_id=${doctorId}`, {
      headers: {
        'uid': props.token,
        'Access-Control-Allow-Origin': '*'
      }
    })
      .then(response => {
        setShiftsData(response.data.shifts);
        setAppointmentType(response.data.flag);
        setIsLoading(false);
      })
      .catch(err => {
        setError('シフトデータの取得に失敗しました');
        setIsLoading(false);
      });
  }, [selectedDate, doctorId, id, props.token, type]);

  // ローカルの日付を "YYYY-MM-DD" 形式で取得するヘルパー
  const formatLocalDate = (date) => {
    return toJstDateInputValue(date);
  };

  // 「最短のブロック」を計算する（Footer に表示するボタン用）
  const computeEarliestBlock = () => {
    const todayStr = formatLocalDate(new Date());
    const validShifts = shiftsData.filter(shift => {
      const shiftDateStr = formatLocalDate(new Date(shift.start_time));
      // 当日の分は現在時刻＋15分以上のもののみ
      if (shiftDateStr === todayStr) {
        const threshold = new Date(Date.now() + 15 * 60000);
        return new Date(shift.start_time) >= threshold && !shift.digest;
      }
      return shiftDateStr > todayStr && !shift.digest;
    });

    if (validShifts.length === 0) return null;

    const shiftsByDoctor = {};
    validShifts.forEach(shift => {
      if (!shiftsByDoctor[shift.admin_user_id]) {
        shiftsByDoctor[shift.admin_user_id] = [];
      }
      shiftsByDoctor[shift.admin_user_id].push(shift);
    });

    let allValidBlocks = [];
    const requiredShifts = appointmentType === 'initial' ? 3 : 2;

    Object.entries(shiftsByDoctor).forEach(([docId, doctorShifts]) => {
      doctorShifts.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      const groups = groupConsecutiveShifts(doctorShifts);
      groups.forEach(group => {
        let i = 0;
        while (i + requiredShifts <= group.length) {
          const neededShifts = group.slice(i, i + requiredShifts);
          const all_time = neededShifts.map(shift => shift.start_time);
          const formattedStartTime = formatTime(neededShifts[0].start_time);
          const endTime = formatTime(
            addMinutesToDateTime(
              neededShifts[neededShifts.length - 1].start_time,
              5
            )
          );
          allValidBlocks.push({
            id: neededShifts[0].id,
            startTime: formattedStartTime,
            endTime: endTime,
            start_time: all_time[0],
            shiftIds: neededShifts.map(shift => shift.id),
            admin_user_id: parseInt(docId, 10),
            appointmentType: appointmentType,
            subject_id: id,
          });
          i += requiredShifts;
        }
      });
    });

    if (allValidBlocks.length === 0) return null;

    // 全医師モードなら同開始時刻でランキング順で絞るロジックを入れても良い
    // （省略）

    allValidBlocks.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    return allValidBlocks[0];
  };

  const earliestBlock = computeEarliestBlock();

  // ─── ここから "時間帯ボタン" を押したときにスクロールする関数 ───
  const scrollToSection = (targetRef) => {
    console.log('scrollToSection called', { targetRef, scheduleContainerRef });
    
    if (targetRef.current) {
      console.log('Target found:', targetRef.current);
      
      // まず、ウィンドウ全体でのスクロールを試す
      const target = targetRef.current;
      const targetRect = target.getBoundingClientRect();
      const headerHeight = 280; // CSSで設定されているヘッダーの高さ
      
      console.log('Target rect:', targetRect);
      console.log('Current window scroll:', window.scrollY);
      
      // ウィンドウ全体をスクロール
      const scrollToPosition = window.scrollY + targetRect.top - headerHeight;
      
      console.log('Scrolling window to position:', scrollToPosition);
      
      window.scrollTo({
        top: scrollToPosition,
        behavior: 'smooth'
      });
    } else {
      console.log('Target ref not found');
    }
  };
  // ───────────────────────────────────────────────────────────────────

  // 予約成立時のダミー処理
  const handleReservation = (block) => {
    // 実際のリクエストや状態更新はここで行う想定
    const updatedShiftsData = shiftsData.map(shift => (
      block.shiftIds.includes(shift.id)
        ? { ...shift, digest: true }
        : shift
    ));
    setShiftsData(updatedShiftsData);
  };

  // 週ナビゲーションヘッダー
  const renderWeekNavigation = () => {
    const headerMonth = selectedDate
      ? `${selectedDate.getFullYear()}年${selectedDate.getMonth() + 1}月`
      : '';
    return (
      <div className="reserve_schedule_week_navigation">
        <button
          className={`reserve_schedule_nav_button ${weekOffset === 0 ? 'disabled' : ''}`}
          onClick={() => weekOffset > 0 && setWeekOffset(weekOffset - 1)}
          disabled={weekOffset === 0}
        >
          <ChevronLeft className="reserve_schedule_nav_icon" />
          <span>前の週</span>
        </button>
        <div className="reserve_schedule_month_display">
          <Calendar className="reserve_schedule_month_icon" />
          <span>{headerMonth}</span>
        </div>
        <button
          className="reserve_schedule_nav_button"
          onClick={() => setWeekOffset(weekOffset + 1)}
        >
          <span>次の週</span>
          <ChevronRight className="reserve_schedule_nav_icon" />
        </button>
      </div>
    );
  };

  // 日付ヘッダー
  const renderDateHeader = () => {
    const today = new Date();
    const todayStr = formatLocalDate(today);
    return (
      <div className="reserve_schedule_date_header">
        {headerDates.map((date, idx) => {
          const dateStr = formatLocalDate(date);
          const isToday = dateStr === todayStr;
          const isSelected = formatLocalDate(date) === formatLocalDate(selectedDate);
          const isPast = dateStr < todayStr;
          const dayOfWeek = date.getDay();
          const isSaturday = dayOfWeek === 6;
          const isSunday = dayOfWeek === 0;
          
          return (
            <div
              key={idx}
              className={`reserve_schedule_date_cell ${isSelected ? 'selected' : ''} ${isPast ? 'past' : ''} ${isSaturday ? 'saturday' : ''} ${isSunday ? 'sunday' : ''}`}
              onClick={() => {
                if (dateStr >= todayStr) setSelectedDate(date);
              }}
            >
              <span className="reserve_schedule_day_label">
                {["日", "月", "火", "水", "木", "金", "土"][date.getDay()]}
              </span>
              <span className="reserve_schedule_date_number">{date.getDate()}</span>
              {isToday && <div className="reserve_schedule_today_indicator" />}
            </div>
          );
        })}
      </div>
    );
  };

  // 医師選択ドロップダウン
  const renderSelectionOptions = () => {
    return (
      <div className="reserve_schedule_doctor_selection">
        <div className="reserve_schedule_doctor_wrapper">
          <User className="reserve_schedule_doctor_icon" />
          <label className="reserve_schedule_doctor_label">担当医師:</label>
          <select
            value={doctorId}
            onChange={(e) => setDoctorId(e.target.value)}
            className="reserve_schedule_doctor_select"
          >
            <option value="any">全医師</option>
            {doctors.map(doctor => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  // 日時表示コンポーネント（Footer の「最短予約」向け）
  const DateTimeComponent = ({ isoDate }) => {
    const date = new Date(isoDate);
    const formattedDate = date.toLocaleDateString('ja-JP', {
      month: '2-digit',
      day: '2-digit',
      weekday: 'short'
    });
    const formattedTime = date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return (
      <div className="reserve_schedule_footer_datetime">
        <Calendar className="reserve_schedule_footer_datetime_icon" />
        <span>{`${formattedDate} ${formattedTime}`}~</span>
      </div>
    );
  };

  const timeIntervals = generateTimeIntervals();

  const renderScheduleTable = () => {
    const today = new Date();
    const todayStr = toJstDateInputValue(today);
    const selectedDateStr = toJstDateInputValue(selectedDate);

    // 当日なら、現在時刻より古い時間帯を除外
    const displayedIntervals = (todayStr === selectedDateStr)
      ? timeIntervals.filter(interval => parseInt(interval.start.split(':')[0]) >= today.getHours())
      : timeIntervals;

    return (
      <div
        style={{
          paddingTop: '20px',
          paddingBottom:'100px'
        }}
      >
        {displayedIntervals.map((interval, idx) => {
          let rowRef = null;
          const hour = parseInt(interval.start.split(":")[0], 10);

          // 時間帯先頭行に対応する ref を振る
          if (hour === 8) {
            rowRef = morningRef;
          } else if (hour === 12) {
            rowRef = afternoonRef;
          } else if (hour === 18) {
            rowRef = eveningRef;
          }

          // この時間帯に対応するブロックを取得
          const availableBlocks = getAvailableBlocks(interval);

          return (
            <div
              key={idx}
              ref={rowRef}       // ←行要素に ref を紐づけ
              style={{
                display: 'flex',
                borderBottom: '1px solid #eee',
                padding: '10px 0',
                alignItems: 'center',
                minHeight: '60px' // 最小高さを設定
              }}
              data-hour={hour} // デバッグ用
            >
              <div style={{ width: '20%', fontWeight: 'bold' }}>
                {interval.label}
              </div>
              <div style={{ flex: 1, padding: '5px' }}>
                {availableBlocks.length > 0 ? (
                  <div className="reservation_time_container">
                    {availableBlocks.map((block, i) => (
                      <div
                        key={i}
                        className="reservation_time_container_available"
                        style={{
                          cursor: 'pointer',
                          margin: '4px',
                          padding: '4px',
                          borderRadius: '4px',
                          display: 'inline-block',
                          fontWeight: 'bold'
                        }}
                        onClick={() => handleReservation(block)}
                      >
                        <NavLink
                          to={{
                            pathname: `/reserve/confirm/${block.id}`
                          }}
                          state={{
                            block: block,
                            admin_user_id: block.admin_user_id,
                            start_time: block.start_time,
                            id: block.id,
                            appointmentType: appointmentType,
                            subject_id: id
                          }}
                          className="has-shift_a"
                        >
                          <span style={{ color: 'red', fontWeight: 'bold' }}>◎</span>
                          {block.startTime} 〜 {block.endTime}
                        </NavLink>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      color: 'black',
                      textAlign: 'center',
                      width: '95%',
                      background: 'rgb(242, 242, 242)',
                      padding: '6px 0px',
                      borderRadius:'6px'
                    }}
                  >
                    ×
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // 5分刻みシフト → 「連続ブロック」の取得
  const getAvailableBlocks = (interval) => {
    let availableShifts = shiftsData.filter(shift => !shift.digest);
    availableShifts = filterShiftsByInterval(availableShifts, interval.start, interval.end);

    // 「当日かつ現在時刻より 20 分前までは予約不可」
    const todayStr = formatLocalDate(new Date());
    const selectedDateStr = formatLocalDate(selectedDate);
    if (todayStr === selectedDateStr) {
      const threshold = new Date(Date.now() + 20 * 60 * 1000);
      availableShifts = availableShifts.filter(shift => new Date(shift.start_time) >= threshold);
    }

    const shiftsByDoctor = {};
    availableShifts.forEach(shift => {
      if (!shiftsByDoctor[shift.admin_user_id]) {
        shiftsByDoctor[shift.admin_user_id] = [];
      }
      shiftsByDoctor[shift.admin_user_id].push(shift);
    });

    let allValidGroups = [];
    const requiredShifts = appointmentType === 'initial' ? 3 : 2;

    Object.entries(shiftsByDoctor).forEach(([docId, doctorShifts]) => {
      doctorShifts.sort((a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      );
      const groups = groupConsecutiveShifts(doctorShifts);

      groups.forEach(group => {
        let i = 0;
        while (i + requiredShifts <= group.length) {
          const neededShifts = group.slice(i, i + requiredShifts);
          const formattedStartTime = formatTime(neededShifts[0].start_time);
          const endTime = formatTime(
            addMinutesToDateTime(
              neededShifts[neededShifts.length - 1].start_time,
              5
            )
          );
          const all_time = neededShifts.map(s => s.start_time);

          allValidGroups.push({
            id: neededShifts[0].id,
            startTime: formattedStartTime,
            endTime: endTime,
            start_time: all_time[0],
            shiftIds: neededShifts.map(s => s.id),
            admin_user_id: parseInt(docId, 10),
            appointmentType,
            subject_id: id
          });
          i += requiredShifts;
        }
      });
    });

    // 全医師モード時の重複潰しロジック（任意）
    if (doctorId === "any") {
      const dedupedMap = {};
      allValidGroups.forEach(block => {
        const key = `${block.startTime}-${block.endTime}`;
        const doc = doctors.find(d => d.id === block.admin_user_id);
        const rank = doc ? doc.ranking : Infinity;
        if (!dedupedMap[key] || rank < dedupedMap[key].rank) {
          dedupedMap[key] = { block, rank };
        }
      });
      allValidGroups = Object.values(dedupedMap).map(entry => entry.block);
    }

    allValidGroups.sort((a, b) => {
      if (a.startTime < b.startTime) return -1;
      if (a.startTime > b.startTime) return 1;
      return a.admin_user_id - b.admin_user_id;
    });
    return allValidGroups;
  };

  if (isLoading) {
    return (
      <div>
        <div className="loader" style={{ width: '100px', height: '100px' }}></div>
        <div
          style={{
            width: '100%',
            position: 'absolute',
            bottom: '0%',
            height: '80px',
            background: '#FEFBF9',
            zIndex: '12',
          }}
        />
      </div>
    );
  }

  return (
    <div className="reserve_schedule_main_wrapper">
      <div className="reserve_schedule_header_container">
        {renderSelectionOptions()}
        {renderWeekNavigation()}
        {renderDateHeader()}
        
        {/* 時間帯ナビゲーションボタン */}
        <div className="reserve_schedule_time_nav">
          <button
            className="reserve_schedule_time_nav_button morning"
            onClick={() => scrollToSection(morningRef)}
          >
            <Sunrise className="reserve_schedule_time_nav_icon" />
            <span>午前 (8-12)</span>
          </button>
          <button
            className="reserve_schedule_time_nav_button afternoon"
            onClick={() => scrollToSection(afternoonRef)}
          >
            <Sun className="reserve_schedule_time_nav_icon" />
            <span>午後 (12-18)</span>
          </button>
          <button
            className="reserve_schedule_time_nav_button evening"
            onClick={() => scrollToSection(eveningRef)}
          >
            <Moon className="reserve_schedule_time_nav_icon" />
            <span>夜間 (18-24)</span>
          </button>
        </div>
      </div>

      {/* スケジュール表全体をスクロール可能にするコンテナ */}
      <div className="reserve_schedule_content_wrapper">
        {renderScheduleTable()}
      </div>

      {/* Footer：最短ブロックへのリンク or 予約不可メッセージ */}
      {earliestBlock ? (
        <div className="footer_content_page">
          <NavLink
            to={`/reserve/confirm/${earliestBlock.id}`}
            className="reserve_schedule_footer_link"
            state={{
              admin_user_id: earliestBlock.admin_user_id,
              start_time: earliestBlock.start_time,
              id: earliestBlock.id,
              block: {
                shiftIds: earliestBlock.shiftIds,
              },
              subject_id: id,
              appointmentType: appointmentType
            }}
          >
            <button className="reserve_schedule_earliest_button">
              <div className="reserve_schedule_earliest_content">
                <span className="reserve_schedule_earliest_title">最短の日付で予約する</span>
                <DateTimeComponent isoDate={earliestBlock.start_time} />
              </div>
            </button>
          </NavLink>
        </div>
      ) : (
        <div className="footer_content_page">
          <button className="reserve_schedule_no_available_button">
            <AlertCircle className="reserve_schedule_no_available_icon" />
            <span className="reserve_schedule_no_available_text">
              本日予約可能な時間帯はありません
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ReserveTakeContainer;